import type {
  BuildTopicClustersOptions,
  ClusterOrigin,
  ConfidenceBand,
  KnowledgeBrain,
  KnowledgeBrainSummary,
  TopicCluster,
} from "@/types/knowledge/knowledge-brain";
import type { DocumentWithMetadata } from "@/types/knowledge/knowledge-base";

/**
 * Client-side topic clustering for the Knowledge Base "brain" view.
 *
 * IMPORTANT — this is an honest approximation, not semantic clustering.
 * It only uses data the frontend already holds:
 *
 *   1. `folder` — an explicit human grouping. Always the strongest signal, so
 *      foldered documents cluster by folder.
 *   2. Salient terms shared by two or more document names / descriptions.
 *   3. Everything left over collapses into one "Unsorted" cluster.
 *
 * It does NOT read document bodies (they live behind per-document signed URLs)
 * and it does NOT embed anything. A server-side clustering pass over the parsed
 * text would produce meaningfully better topics — see
 * `docs/handoff/answer-to-document-logging.md` for the related backend ask.
 */

const DEFAULT_STALE_DAYS = 180;
const DEFAULT_MAX_CLUSTERS = 12;
const MIN_TERM_LENGTH = 4;
const MIN_DOCS_PER_TERM = 2;

const STOP_WORDS = new Set([
  "about", "after", "again", "against", "all", "also", "and", "any", "are", "because", "been",
  "before", "being", "between", "both", "but", "copy", "could", "did", "does", "doing", "done",
  "down", "draft", "during", "each", "final", "for", "from", "further", "has", "have", "having",
  "her", "here", "hers", "him", "his", "how", "info", "information", "into", "its", "itself",
  "just", "latest", "more", "most", "new", "not", "note", "notes", "now", "off", "once", "only",
  "other", "our", "ours", "out", "over", "own", "page", "pages", "pdf", "revised", "same", "she",
  "should", "some", "such", "than", "that", "the", "their", "theirs", "them", "then", "there",
  "these", "they", "this", "those", "through", "too", "under", "until", "updated", "use", "used",
  "very", "was", "were", "what", "when", "where", "which", "while", "who", "whom", "why", "will",
  "with", "would", "you", "your", "yours", "document", "documents", "file", "files", "version",
  "final", "untitled", "temp", "test",
]);

const stripExtension = (name: string) => name.replace(/\.[a-z0-9]{1,5}$/i, "");

const titleCase = (term: string) =>
  term
    .split(/[\s-]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

/** Crude singularisation so "policies"/"policy" and "prices"/"price" land together. */
const singularise = (word: string): string => {
  if (word.length <= MIN_TERM_LENGTH) return word;
  if (word.endsWith("ies")) return `${word.slice(0, -3)}y`;
  if (word.endsWith("sses") || word.endsWith("shes") || word.endsWith("ches")) {
    return word.slice(0, -2);
  }
  if (word.endsWith("s") && !word.endsWith("ss")) return word.slice(0, -1);
  return word;
};

export const documentTerms = (doc: DocumentWithMetadata): string[] => {
  const description =
    typeof doc.metadata?.description === "string" ? doc.metadata.description : "";
  const raw = `${stripExtension(doc.name ?? "")} ${description}`.toLowerCase();
  const seen = new Set<string>();
  const terms: string[] = [];

  for (const token of raw.split(/[^a-z0-9]+/)) {
    if (token.length < MIN_TERM_LENGTH) continue;
    if (/^\d+$/.test(token)) continue;
    const term = singularise(token);
    if (term.length < MIN_TERM_LENGTH) continue;
    if (STOP_WORDS.has(term) || STOP_WORDS.has(token)) continue;
    if (seen.has(term)) continue;
    seen.add(term);
    terms.push(term);
  }

  return terms;
};

const isActivated = (doc: DocumentWithMetadata) =>
  Boolean(doc.el_doc_id) && doc.is_active !== false;

const isAttached = (doc: DocumentWithMetadata) => (doc.dependent_agents?.length ?? 0) > 0;

export const documentAgeDays = (doc: DocumentWithMetadata, now: Date): number | null => {
  const raw = doc.updated_at || doc.created_at;
  if (!raw) return null;
  const at = new Date(raw).getTime();
  if (!Number.isFinite(at)) return null;
  return Math.max(0, (now.getTime() - at) / (1000 * 60 * 60 * 24));
};

export const bandFor = (confidence: number): ConfidenceBand => {
  if (confidence >= 0.7) return "strong";
  if (confidence >= 0.4) return "steady";
  return "thin";
};

/** Colour along the brand gradient: #4E9CFF (confident) → #FF4DB8 (thin). */
export const confidenceColor = (confidence: number): string => {
  const t = Math.min(1, Math.max(0, 1 - confidence));
  const from = { r: 0x4e, g: 0x9c, b: 0xff };
  const to = { r: 0xff, g: 0x4d, b: 0xb8 };
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
  return `#${[mix(from.r, to.r), mix(from.g, to.g), mix(from.b, to.b)]
    .map((v) => v.toString(16).padStart(2, "0"))
    .join("")}`;
};

interface Bucket {
  id: string;
  label: string;
  origin: ClusterOrigin;
  docs: DocumentWithMetadata[];
}

const scoreCluster = (
  docs: DocumentWithMetadata[],
  staleCount: number,
  openQuestionCount: number,
): number => {
  if (docs.length === 0) return 0;
  const activated = docs.filter(isActivated).length / docs.length;
  const attached = docs.filter(isAttached).length / docs.length;
  const fresh = 1 - staleCount / docs.length;
  // Depth: one document on a topic is thin however well it is wired up.
  const depth = Math.min(1, docs.length / 4);

  const base = activated * 0.35 + attached * 0.25 + fresh * 0.2 + depth * 0.2;
  const openQuestionPenalty = Math.min(0.3, openQuestionCount * 0.1);
  return Math.max(0, Math.min(1, base - openQuestionPenalty));
};

/**
 * Groups documents into topic clusters. Folders win; unfoldered documents fall
 * back to shared salient terms; the remainder becomes one "Unsorted" cluster.
 */
export const buildTopicClusters = (
  documents: DocumentWithMetadata[],
  options: BuildTopicClustersOptions = {},
): TopicCluster[] => {
  const {
    openQuestions = [],
    staleAfterDays = DEFAULT_STALE_DAYS,
    maxClusters = DEFAULT_MAX_CLUSTERS,
    now = new Date(),
  } = options;

  if (documents.length === 0) return [];

  const buckets: Bucket[] = [];
  const byFolder = new Map<string, Bucket>();
  const unfoldered: DocumentWithMetadata[] = [];

  for (const doc of documents) {
    const folder = doc.folder;
    if (folder?.id && folder.name) {
      let bucket = byFolder.get(folder.id);
      if (!bucket) {
        bucket = { id: `folder:${folder.id}`, label: folder.name, origin: "folder", docs: [] };
        byFolder.set(folder.id, bucket);
        buckets.push(bucket);
      }
      bucket.docs.push(doc);
    } else {
      unfoldered.push(doc);
    }
  }

  // Term frequency across the unfoldered remainder.
  const termDocs = new Map<string, DocumentWithMetadata[]>();
  for (const doc of unfoldered) {
    for (const term of documentTerms(doc)) {
      const list = termDocs.get(term);
      if (list) list.push(doc);
      else termDocs.set(term, [doc]);
    }
  }

  const claimed = new Set<string>();
  const candidateTerms = [...termDocs.entries()]
    .filter(([, docs]) => docs.length >= MIN_DOCS_PER_TERM)
    .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]));

  for (const [term, docs] of candidateTerms) {
    const unclaimed = docs.filter((d) => !claimed.has(d.id));
    if (unclaimed.length < MIN_DOCS_PER_TERM) continue;
    unclaimed.forEach((d) => claimed.add(d.id));
    buckets.push({
      id: `topic:${term}`,
      label: titleCase(term),
      origin: "topic",
      docs: unclaimed,
    });
  }

  const leftovers = unfoldered.filter((d) => !claimed.has(d.id));
  if (leftovers.length > 0) {
    buckets.push({ id: "unsorted", label: "Unsorted", origin: "unsorted", docs: leftovers });
  }

  const normalisedQuestions = openQuestions.map((q) => q.toLowerCase());

  const clusters: TopicCluster[] = buckets.map((bucket) => {
    const staleCount = bucket.docs.filter((doc) => {
      const age = documentAgeDays(doc, now);
      return age !== null && age > staleAfterDays;
    }).length;

    const labelTerms = bucket.label
      .toLowerCase()
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length >= MIN_TERM_LENGTH && !STOP_WORDS.has(t));

    const openQuestionCount =
      labelTerms.length === 0
        ? 0
        : normalisedQuestions.filter((q) => labelTerms.some((t) => q.includes(t))).length;

    const confidence = scoreCluster(bucket.docs, staleCount, openQuestionCount);

    return {
      id: bucket.id,
      label: bucket.label,
      origin: bucket.origin,
      documentIds: bucket.docs.map((d) => d.id),
      documentCount: bucket.docs.length,
      activatedCount: bucket.docs.filter(isActivated).length,
      attachedCount: bucket.docs.filter(isAttached).length,
      staleCount,
      openQuestionCount,
      confidence,
      band: bandFor(confidence),
    };
  });

  clusters.sort(
    (a, b) =>
      b.documentCount - a.documentCount ||
      a.label.localeCompare(b.label),
  );

  if (clusters.length <= maxClusters) return clusters;

  // Collapse the tail so the map stays readable rather than dropping documents.
  const kept = clusters.slice(0, maxClusters - 1);
  const tail = clusters.slice(maxClusters - 1);
  const tailDocIds = tail.flatMap((c) => c.documentIds);
  const tailConfidence =
    tail.reduce((sum, c) => sum + c.confidence * c.documentCount, 0) /
    Math.max(1, tailDocIds.length);

  kept.push({
    id: "everything-else",
    label: "Everything else",
    origin: "unsorted",
    documentIds: tailDocIds,
    documentCount: tailDocIds.length,
    activatedCount: tail.reduce((sum, c) => sum + c.activatedCount, 0),
    attachedCount: tail.reduce((sum, c) => sum + c.attachedCount, 0),
    staleCount: tail.reduce((sum, c) => sum + c.staleCount, 0),
    openQuestionCount: tail.reduce((sum, c) => sum + c.openQuestionCount, 0),
    confidence: tailConfidence,
    band: bandFor(tailConfidence),
  });

  return kept;
};

const plural = (count: number, one: string, many: string) => (count === 1 ? one : many);

export const summariseBrain = (
  documents: DocumentWithMetadata[],
  clusters: TopicCluster[],
): KnowledgeBrainSummary => {
  const totalDocuments = documents.length;
  const activatedDocuments = documents.filter(isActivated).length;

  const ranked = [...clusters].sort((a, b) => b.confidence - a.confidence);
  const strongestCluster = ranked[0] ?? null;
  const thinnestCluster = ranked.length > 1 ? ranked[ranked.length - 1] : null;

  if (totalDocuments === 0) {
    return {
      totalDocuments: 0,
      activatedDocuments: 0,
      clusterCount: 0,
      strongestCluster: null,
      thinnestCluster: null,
      headline: "Your Genie has nothing to work from yet.",
      detail: "Add your first source below and this map fills in as knowledge lands.",
    };
  }

  const headline = `Your Genie knows ${clusters.length} ${plural(
    clusters.length,
    "topic",
    "topics",
  )} across ${totalDocuments} ${plural(totalDocuments, "source", "sources")}.`;

  const parts: string[] = [];
  if (strongestCluster) {
    parts.push(
      `${strongestCluster.label} is its strongest ground (${strongestCluster.documentCount} ${plural(
        strongestCluster.documentCount,
        "source",
        "sources",
      )}).`,
    );
  }
  if (thinnestCluster && thinnestCluster.band === "thin") {
    parts.push(`${thinnestCluster.label} is thin — worth adding to next.`);
  }
  if (activatedDocuments < totalDocuments) {
    const waiting = totalDocuments - activatedDocuments;
    parts.push(
      `${waiting} ${plural(waiting, "source has", "sources have")} not reached your Genies yet.`,
    );
  }

  return {
    totalDocuments,
    activatedDocuments,
    clusterCount: clusters.length,
    strongestCluster,
    thinnestCluster,
    headline,
    detail: parts.join(" "),
  };
};

export const buildKnowledgeBrain = (
  documents: DocumentWithMetadata[],
  options: BuildTopicClustersOptions = {},
): KnowledgeBrain => {
  const clusters = buildTopicClusters(documents, options);
  return { clusters, summary: summariseBrain(documents, clusters) };
};
